import { useState, useEffect, useRef } from "react";
import "./TypingChallenge.css";

// 🔹 Word banks by difficulty (expanded)

// Easy: common words for beginners (~250 words)
const easyWords = `
cat dog cup pen sun run fun book ball tree lamp red blue green rain milk 
fish bird house car bike hat shoe chair table door wall floor water food 
apple banana orange grape lemon peach pear corn rice bread cake sugar salt 
milk tea coffee juice soup egg cheese butter honey meat fish chicken pizza 
shirt pants socks shoes coat dress skirt jacket tie glove scarf belt watch 
phone key bag wallet coin note card button zipper paper pencil eraser ruler 
desk bench window door roof garden park street road shop store hospital school 
train bus plane boat ship river lake mountain hill valley desert forest jungle 
flower tree grass leaf branch root seed soil rock sand mud clay cloud sky star 
moon sun planet earth weather wind rain snow hail fog storm lightning thunder 
summer winter spring autumn morning night noon evening today tomorrow yesterday 
happy sad angry tired hungry thirsty sick healthy brave smart kind gentle loud 
quiet fast slow small big short long thin thick heavy light soft hard cold warm 
hot cool wet dry bright dark clean dirty rich poor young old new old ancient 
simple complex easy difficult quick slow calm nervous excited funny serious 
`.trim().split(/\s+/);

// Medium: tech, programming, and general vocabulary (~300 words)
const mediumWords = `
coding react javascript laptop mobile qasim cheema gamehub project logic system 
backend frontend api database storage flutter python github firebase server editor 
variable function loop array object component module class interface network cloud 
algorithm design pattern version control debug compile execute terminal console 
json html css node express kotlin swift java typescript sql nosql rest api 
authentication authorization encryption decryption server client request response 
performance optimization caching concurrency thread promise async await recursion 
callback event listener middleware routing framework library package plugin script 
compile execute runtime debugger profiler syntax semantic logic boolean integer float 
string character condition operator expression exception error try catch finally 
stack queue heap tree graph linkedlist hashmap set dictionary tuple arraylist 
iteration recursion function pointer lambda closure higher-order pure side-effect 
immutable mutable prototype inheritance encapsulation polymorphism abstraction 
design principles SOLID DRY KISS YAGNI agile scrum kanban git merge branch commit 
push pull fork clone repository pipeline continuous integration deployment docker 
kubernetes virtualization container orchestration cloud computing serverless microservice 
api gateway load balancer endpoint token key session cookie http https protocol 
latency throughput bandwidth cache compression serialization deserialization logging 
monitoring alerting testing unit integration regression e2e performance stress security 
`.trim().split(/\s+/);

// Hard: advanced, technical, scientific, and academic words (~250+ words)
const hardWords = `
synchronization asynchronous blockchain architecture computation integration 
cybersecurity virtualization optimization neuroprocessing multiphase algorithmic 
heterogeneous multiprocessing cryptography steganography nanotechnology 
bioinformatics astrophysics electrophysiology thermodynamics quantum mechanics 
differential equations microarchitecture parallelization heuristic methodology 
transdisciplinary phenomenology epistemology ontology paradigmatic infrastructure 
heuristics neuroplasticity cognitive load multithreading virtualization scalability 
algorithmic complexity data serialization schema normalization denormalization 
middleware orchestration concurrency pipelining optimization regression analysis 
vector calculus tensor algebra discrete mathematics linear algebra stochastic 
probability inference hypothesis testing confidence interval variance standard deviation 
entropy enthalpy catalysis polymerization electrophoresis chromatography spectroscopy 
fluorescence biochemistry molecular genetics microbiology virology immunology pathology 
oncology neurology endocrinology hematology pharmacology toxicology epidemiology 
climatology seismology volcanology paleontology archaeology anthropology sociology 
psychology linguistics semiotics philosophy logic reasoning deduction induction abduction 
phenomenology existentialism structuralism postmodernism postcolonialism 
transcendentalism utilitarianism deontology consequentialism epistemic ethical metaphysics 
ontology aesthetics epistemology pragmatism rationalism empiricism relativism determinism 
chaos fractal topology manifold differential geometry knot theory group theory ring theory 
field theory category theory homomorphism isomorphism bijection surjection injection 
asymptotic complexity algorithmic randomness computability recursion theory lambda calculus 
neural network deep learning reinforcement learning convolutional recurrent transformer 
attention mechanism backpropagation gradient descent optimization regularization 
dropout batch normalization embedding tokenization natural language processing nlp 
computer vision cv image segmentation object detection generative adversarial network 
gan autoencoder variational autoencoder vae latent space latent variable manifold learning
`.trim().split(/\s+/);

// Combine all
const allWords = [...easyWords, ...mediumWords, ...hardWords];

export default function TypingChallenge() {
  // SETTINGS
  const [mode, setMode] = useState("time"); // "time" or "words"
  const [difficulty, setDifficulty] = useState("mixed"); // mixed / easy / medium / hard
  const [duration, setDuration] = useState(60); // seconds (for time mode)
  const [wordLimit, setWordLimit] = useState(80); // (for words mode)
  const [hardMode, setHardMode] = useState(false); // no backspace
  // sound removed

  // GAME STATE
  const [words, setWords] = useState([]);
  const [input, setInput] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(duration);
  const [started, setStarted] = useState(false);
  const [mistakes, setMistakes] = useState(0); // counts wrong words
  const [finished, setFinished] = useState(false);
  const [currentWordWrong, setCurrentWordWrong] = useState(false); // track wrong chars

  const textRef = useRef(null);

  // Generate words
  const generateWords = (opts = {}) => {
    const usedDifficulty = opts.difficulty ?? difficulty;
    const usedMode = opts.mode ?? mode;
    const usedWordLimit = opts.wordLimit ?? wordLimit;

    let bank =
      usedDifficulty === "easy"
        ? easyWords
        : usedDifficulty === "medium"
        ? mediumWords
        : usedDifficulty === "hard"
        ? hardWords
        : allWords;

    const random = [];
    const count = usedMode === "time" ? Math.max(150, usedWordLimit) : usedWordLimit;
    for (let i = 0; i < count; i++) {
      random.push(bank[Math.floor(Math.random() * bank.length)]);
    }
    setWords(random);
  };

  // regenerate words when settings change
  useEffect(() => {
    generateWords({ difficulty, mode, wordLimit });
    setInput("");
    setCurrentIndex(0);
    setCharIndex(0);
    setMistakes(0);
    setFinished(false);
    setStarted(false);
    setCurrentWordWrong(false);
    setTimeLeft(duration);
  }, [difficulty, wordLimit, mode, duration]);

  // Timer
  useEffect(() => {
    if (mode !== "time" || !started || finished) return;
    if (timeLeft <= 0) return setFinished(true);

    const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [started, timeLeft, finished, mode]);

  // Smooth scroll so current word is always visible
  useEffect(() => {
    if (!textRef.current) return;

    const currentWordSpan = textRef.current.children[currentIndex];
    if (currentWordSpan) {
      const wordTop = currentWordSpan.offsetTop;
      const wordBottom = wordTop + currentWordSpan.offsetHeight;
      const containerTop = textRef.current.scrollTop;
      const containerBottom = containerTop + textRef.current.offsetHeight;

      if (wordBottom > containerBottom) {
        // Scroll down
        textRef.current.scrollTop = wordBottom - textRef.current.offsetHeight + 4;
      } else if (wordTop < containerTop) {
        // Scroll up
        textRef.current.scrollTop = wordTop - 4;
      }
    }
  }, [currentIndex]);

  // handle typing input
  const handleTyping = (e) => {
    const value = e.target.value;

    // Block backspace in hard mode
    if (hardMode && value.length < input.length) return;

    if (!started) setStarted(true);

    setInput(value);
    setCharIndex(value.length);

    const currentWord = words[currentIndex] || "";
    if (value.length > 0 && value[value.length - 1] !== currentWord[value.length - 1]) {
      setCurrentWordWrong(true);
    }

    // When user finishes a word (presses space)
    if (value.endsWith(" ")) {
      const typedWord = value.trim();
      if (typedWord !== currentWord) setMistakes((m) => m + 1);

      setCurrentIndex((i) => i + 1);
      setInput("");
      setCharIndex(0);
      setCurrentWordWrong(false);

      if (mode === "words" && currentIndex + 1 >= wordLimit) setFinished(true);

      if (currentIndex + 2 >= words.length) generateWords();
    }
  };

  // Stats
  const timeSpent = Math.max(1, duration - timeLeft);
  const wpm = Math.round((currentIndex / timeSpent) * 60);
  const accuracy =
    currentIndex > 0
      ? Math.max(0, Math.round(((currentIndex - mistakes) / currentIndex) * 100))
      : 100;

  // Restart
  const restart = () => {
    generateWords();
    setInput("");
    setCurrentIndex(0);
    setCharIndex(0);
    setMistakes(0);
    setTimeLeft(duration);
    setStarted(false);
    setFinished(false);
    setCurrentWordWrong(false);
  };

  // update timeLeft when duration changes
  useEffect(() => setTimeLeft(duration), [duration]);

  return (
    <div className="typing-wrapper">
      {/* SETTINGS */}
      {!started && !finished && (
        <div className="settings-box">
          <h2>Typing Practice Settings</h2>

          <div className="setting">
            <label>Mode:</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="time">Time Test</option>
              <option value="words">Words Test</option>
            </select>
          </div>

          {mode === "time" ? (
            <div className="setting">
              <label>Duration (seconds):</label>
              <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={60}>60</option>
                <option value={120}>120</option>
              </select>
            </div>
          ) : (
            <div className="setting">
              <label>Word Limit:</label>
              <select value={wordLimit} onChange={(e) => setWordLimit(Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          )}

          <div className="setting">
            <label>Difficulty:</label>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
              <option value="mixed">Mixed</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="setting">
            <label>
              <input
                type="checkbox"
                checked={hardMode}
                onChange={() => setHardMode((v) => !v)}
              />
              {" "}No Backspace (Hard Mode)
            </label>
          </div>

          <p>Click the input box to start the test.</p>
        </div>
      )}

      {/* Timer */}
      {mode === "time" && (
        <div className="progress-bar" aria-hidden>
          <div
            className="progress-fill"
            style={{ width: `${(timeLeft / duration) * 100}%` }}
          />
        </div>
      )}

      {/* Words display */}
      <div className="typing-words" ref={textRef}>
        {words.map((word, index) => {
          const isCurrent = index === currentIndex;
          return (
            <span
              key={index}
              className={
                !isCurrent && index < currentIndex
                  ? "typed-word"
                  : isCurrent
                  ? currentWordWrong
                    ? "wrong-word current-word"
                    : "current-word"
                  : ""
              }
            >
              {word.split("").map((char, i) => {
                let charClass = "";
                if (isCurrent) {
                  if (i < input.length) charClass = input[i] === char ? "correct-char" : "wrong-char";
                  else if (i === input.length) charClass = "current-char";
                }
                return <b key={i} className={charClass}>{char}</b>;
              })}
            </span>
          );
        })}
      </div>

      {/* Input */}
      <input
        type="text"
        className="typing-input"
        value={input}
        onChange={handleTyping}
        disabled={finished}
        placeholder="Click here and start typing..."
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
      />

      {hardMode && !finished && (
        <div className="input-hint">Hard Mode enabled — Backspace is disabled.</div>
      )}

      {/* Stats */}
      <div className="typing-stats">
        <p>⏳ Time Left: {mode === "time" ? timeLeft : "∞"}</p>
        <p>⚡ WPM: {wpm}</p>
        <p>🎯 Accuracy: {accuracy}%</p>
        <p>❌ Mistakes: {mistakes}</p>
      </div>

      {/* Result */}
      {finished && (
        <div className="typing-result">
          <h2>🏁 Results</h2>
          <p>WPM: {wpm}</p>
          <p>Accuracy: {accuracy}%</p>
          <p>Mistakes: {mistakes}</p>

          <button className="restart-btn" onClick={restart}>Restart</button>
        </div>
      )}
    </div>
  );
}
